import { useContext } from 'react'

import {
  Button, Form, LoadingModule, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import SelectQuotes from './components/SelectQuotes'
import useCreateFinanceApplicationModal from './hooks/useCreateFinanceApplicationModal'

const MODAL_KEY = 'CreateFinanceApplicationModal'

type ModalCallbacks = {
  closeModal: () => void,
  createFinanceApplication: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateFinanceApplicationModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, createFinanceApplication } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, `${MODAL_KEY}`, {})
  const { campaign } = modalPayload

  const createFinanceApplicationModalPayload = useCreateFinanceApplicationModal(campaign)

  const {
    callbacks: {
      createFn,
    },
    creating,
    filteredAgencyPaymentMethods,
    financeApplicationForm,
    hasFilteredCampaignQuotes,
    loadingQuotes,
    paymentMethods,
  } = createFinanceApplicationModalPayload

  const {
    entityState,
    entityState: {
      agency_payment_method_id,
      create_contribution,
      total,
    },
    errors,
    handlers,
    saveEnabled,
  } = financeApplicationForm

  const createFinanceApplicationPayload = {
    callbacks: {
      action: createFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    requestOptions: {
      create_contribution,
      include_finance_application_campaign_contribution: true,
    },
    shouldRedirect: true,
    toastText: 'Finance Application Created',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Create Finance" titleSecondLine="Application" />

      <SidebarModal.Content>
        <Form>
          <LoadingModule loading={loadingQuotes} />

          <SelectQuotes createFinanceApplicationModalPayload={createFinanceApplicationModalPayload} />

          <Form.Field
            errorMessage={errors.agency_payment_method_id}
            label="* Payment Method"
            marginTop={hasFilteredCampaignQuotes ? 'large' : ''}
          >
            {/*
             // @ts-expect-error TS2322 */}
            <select
              name="agency_payment_method_id"
              value={agency_payment_method_id}
              {...handlers}
            >
              <option value="">Please Select...</option>
              {filteredAgencyPaymentMethods.map((agencyPaymentMethod) => {
                const { id, payment_method_id } = agencyPaymentMethod

                // @ts-expect-error TS2538
                const paymentMethod = paymentMethods[payment_method_id] || {}
                const processingApi = digObject(
                  paymentMethod,
                  'data.processing_api',
                )

                if (processingApi){
                  // IMPORTANT - Be careful here.
                  // Returning the agencyPaymentMethod ID, not paymentMethod ID
                  return (
                    <option key={id} value={id}>
                      {paymentMethod.title}
                    </option>
                  )
                }

                return null
              })}
            </select>
          </Form.Field>

          <Form.Field errorMessage={errors.total} label="* Finance Amount" marginTop="large">
            <input
              name="total"
              type="number"
              value={total}
              {...handlers}
            />
          </Form.Field>

          <Form.Field label="Signing Method" marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <select
              data-value-type="boolean"
              name="in_person"
              {...handlers}
            >
              <option value="">Please Select...</option>
              <option value="false">Remote</option>
              <option value="true">In Person</option>
            </select>
          </Form.Field>

          <Form.Field label="Create Contribution?" marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <select
              data-value-type="boolean"
              name="create_contribution"
              value={create_contribution}
              {...handlers}
            >
              <option value="">Please Select...</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </Form.Field>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating}
          onClick={() => createFinanceApplication(createFinanceApplicationPayload)}
          size="large"
        >
          Process Payment
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateFinanceApplicationModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
